import { httpsCallable } from 'firebase/functions';
import { Transaction, Connection } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { auth, anonymousAuthReady, functions } from '../firebaseConfig';
import { openPublishSessionForWallet } from './auth';
let callable = null;
const GENESIS = { devnet: 'EtWTRABZaYq6iMfeYKouRu166VU2xqa1wcaWoxPkrZBG', 'mainnet-beta': '5eykt4UsFv8P8NJdTREpY1vzqKqZKvdpKuc147dw2N9d' };
export async function authenticatePublishingWallet(wallet, { isCurrent = () => true, forceSignature = false } = {}) {
    await anonymousAuthReady;
    const uid = wallet.publicKey?.toBase58?.();
    if (!uid || !isCurrent()) throw new Error('Connect your current wallet first.');
    const token = auth.currentUser?.uid === uid ? await auth.currentUser.getIdTokenResult() : null;
    if (forceSignature || token?.claims?.walletVerified !== true) await openPublishSessionForWallet(wallet, 'us-central1', { isCurrent });
    if (!isCurrent() || wallet.publicKey?.toBase58?.() !== uid || auth.currentUser?.uid !== uid) throw new Error('Wallet changed. Please retry.');
    return uid;
}
export async function callDigitalAssets(action, payload = {}, wallet = null) {
    if (wallet) await authenticatePublishingWallet(wallet);
    if (!callable) callable = httpsCallable(functions, 'digitalAssets', { timeout: 540000 });
    return (await callable({ ...payload, action })).data;
}
export function formatAtomic(value, decimals = 9) {
    if (decimals === 0) return String(value || '0');
    const digits = String(value || '0').padStart(decimals + 1, '0');
    return digits.slice(0, -decimals) + (digits.slice(-decimals).replace(/0+$/, '') ? '.' + digits.slice(-decimals).replace(/0+$/, '') : '');
}
export function publishingOperationKey(uid, scope) { return 'muulti-publishing:' + uid + ':' + scope; }
export function pendingPublishingOperation(uid, scope) {
    try { return sessionStorage.getItem(publishingOperationKey(uid, scope)) || ''; } catch { return ''; }
}
export async function assertPublishingNetwork(cluster, endpoint = process.env.REACT_APP_RPC_ENDPOINT) {
    if (!GENESIS[cluster]) throw new Error('Choose a supported publishing network: devnet or mainnet-beta.');
    if (!endpoint) throw new Error('Configure the app RPC before publishing.');
    const connection = new Connection(endpoint, 'finalized');
    let genesis;
    try { genesis = await connection.getGenesisHash(); }
    catch (error) {
        if (/\b(?:401|403)\b/.test(error?.message || '')) {
            throw new Error('The Solana RPC rejected this app. Check the RPC credentials and allowed website origins.');
        }
        throw new Error('The Solana RPC could not be reached. Check its connection before publishing.');
    }
    if (genesis !== GENESIS[cluster]) {
        const actual = Object.keys(GENESIS).find(network => GENESIS[network] === genesis) || 'an unsupported network';
        throw new Error(`The app RPC is on ${actual}; publishing is configured for ${cluster}. Make both networks match before uploading.`);
    }
    return connection;
}
export async function executePublishingOperation(wallet, prepared, scope, onProgress = () => {}) {
    const uid = await authenticatePublishingWallet(wallet);
    const key = publishingOperationKey(uid, scope);
    try { sessionStorage.setItem(key, prepared.requestId); } catch {}
    let result = prepared;
    if (result.status === 'PREPARED') {
        await assertPublishingNetwork(result.cluster);
        const transaction = Transaction.from(Buffer.from(result.transaction, 'base64'));
        if (transaction.feePayer?.toBase58() !== uid) throw new Error('Unexpected publishing payer.');
        const allowed = new Set(['11111111111111111111111111111111', 'CoREENxT6tW1HoK8ypY1SxRMZTcVPm7R94rH4PZNhX7d', 'BGUMAp9Gq7iTEuizy4pqaxsTyUCBK68MDfK752saRPUY', 'ComputeBudget111111111111111111111111111111']);
        if (transaction.instructions.some(instruction => !allowed.has(instruction.programId.toBase58()))) throw new Error('Unexpected publishing instruction.');
        const signTransaction = wallet.signTransaction || wallet.wallet?.adapter?.signTransaction?.bind(wallet.wallet.adapter);
        if (!signTransaction) throw new Error('This wallet does not support transaction signing.');
        const expectedMessage = Buffer.from(transaction.serializeMessage());
        onProgress('Approve the publishing transaction in your wallet.');
        const signed = await signTransaction(transaction);
        if (!Buffer.from(signed.serializeMessage()).equals(expectedMessage)) throw new Error('Your wallet changed the prepared publishing transaction. Keep the app-provided transaction settings and retry.');
        if (wallet.publicKey?.toBase58?.() !== uid) throw new Error('Wallet changed. Please retry.');
        result = await callDigitalAssets('submitOperation', { requestId: result.requestId, signedTransaction: signed.serialize({ requireAllSignatures: false, verifySignatures: false }).toString('base64') }, wallet);
    }
    for (let attempt = 0; attempt < 25 && !['CONFIRMED', 'FAILED', 'EXPIRED'].includes(result.status); attempt += 1) {
        onProgress(result.error || 'Waiting for transaction confirmation. You can retry safely if this takes longer.');
        await new Promise(resolve => setTimeout(resolve, 2000));
        result = await callDigitalAssets('confirmOperation', { requestId: result.requestId }, wallet);
        if (result.status === 'PREPARED') return executePublishingOperation(wallet, result, scope, onProgress);
    }
    if (result.status === 'CONFIRMED' || (scope !== 'collection' && ['FAILED', 'EXPIRED'].includes(result.status))) {
        try { sessionStorage.removeItem(key); } catch {}
    }
    if (result.status === 'FAILED' || result.status === 'EXPIRED') throw new Error(result.error || 'The transaction did not complete. Retry to prepare a new transaction.');
    if (result.status === 'CONFIRMED') window.dispatchEvent(new Event('muulti-publishing-confirmed'));
    return result;
}

export async function continueIrysArchive(wallet, result, resume, onProgress) {
    const irys = await import('./irysPublishing');
    return irys.continueIrysArchive(wallet, result, resume, onProgress);
}
